import type { GameState, Facing, PieceType, Move, Piece } from './types'

const ADVANCE: Record<Facing, [number, number]> = {
  UP:    [0, -1],
  DOWN:  [0, +1],
  LEFT:  [-1, 0],
  RIGHT: [+1, 0],
}

const PIECE_VALUE: Record<PieceType, number> = {
  queen:  9,
  rook:   5,
  bishop: 3.5,
  knight: 3,
  pawn:   1,
  king:   0,
}

/**
 * Resolve all pending moves in simultaneous_resolution mode.
 *
 * Conflict rules:
 *   1. Mutual capture: A.to === B.from && B.to === A.from → both pieces removed, neither moves
 *   2. Contested destination: two+ pieces going to same square → highest piece value wins; tiebreak = lower player index
 *   3. Target moved away: piece targets a square whose occupant is simultaneously moving away → mover arrives at vacated square (no capture)
 *
 * All non-conflicting moves are applied ATOMICALLY — all movers' FROM squares
 * are cleared before any piece is placed at a TO square. This means a piece
 * moving away cannot be captured by a piece targeting its origin in the same round.
 *
 * Returns a new GameState with moves resolved. Clears pendingMoves. Increments turnNumber once.
 */
export function resolvePendingMoves(state: GameState): GameState {
  // Step 1: filter to valid pending moves (piece still at 'from', owned by player)
  const valid = state.pendingMoves.filter(m => {
    const sq = state.squares[m.from]
    return sq?.piece != null && sq.piece.owner === m.player
  })

  // Sort ascending by player index for deterministic tiebreaking
  const sorted = [...valid].sort((a, b) => a.player - b.player)

  // Step 2: detect mutual captures (A.to === B.from && B.to === A.from)
  const mutualPlayers = new Set<number>()
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      const a = sorted[i]
      const b = sorted[j]
      if (a.to === b.from && b.to === a.from) {
        mutualPlayers.add(a.player)
        mutualPlayers.add(b.player)
      }
    }
  }

  // Step 3: resolve contested destinations among non-mutual moves
  // Winner = highest piece value; tiebreak = lower player index (sorted order)
  const nonMutual = sorted.filter(m => !mutualPlayers.has(m.player))
  const destMovers = new Map<string, Move[]>()
  for (const m of nonMutual) {
    if (!destMovers.has(m.to)) destMovers.set(m.to, [])
    destMovers.get(m.to)!.push(m)
  }

  const allowedPlayers = new Set<number>()
  for (const [, movers] of destMovers) {
    let winner = movers[0]
    let winnerValue = PIECE_VALUE[state.squares[winner.from].piece!.type]
    for (const m of movers.slice(1)) {
      const val = PIECE_VALUE[state.squares[m.from].piece!.type]
      if (val > winnerValue) {
        winner = m
        winnerValue = val
      }
    }
    allowedPlayers.add(winner.player)
    // Losers stay put (not added to allowedPlayers)
  }

  const toApply = sorted.filter(m => allowedPlayers.has(m.player))
  const mutualMoves = sorted.filter(m => mutualPlayers.has(m.player))

  // Step 4: Apply changes ATOMICALLY
  const newSquares = { ...state.squares }
  const newPlayers = state.players.map(p => ({ ...p }))
  const newCapturedBy: Record<number, PieceType[]> = { ...state.capturedBy }

  // 4a: Clear all en passant targets (re-set below for double pawn advances)
  for (const p of newPlayers) {
    p.enPassantTarget = null
  }

  // 4b: Mutual captures — remove both pieces from their FROM squares (neither moves)
  for (const m of mutualMoves) {
    const capturedPiece = state.squares[m.from]?.piece  // read from original snapshot
    if (capturedPiece) {
      const otherMove = mutualMoves.find(
        other => other.player !== m.player && other.to === m.from && other.from === m.to
      )
      if (otherMove) {
        if (!newCapturedBy[otherMove.player]) newCapturedBy[otherMove.player] = []
        newCapturedBy[otherMove.player] = [...newCapturedBy[otherMove.player], capturedPiece.type]
      }
      newSquares[m.from] = { ...newSquares[m.from], piece: null }
    }
  }

  // 4c: Pre-clear ALL allowed movers' FROM squares simultaneously.
  // This is the key to correct simultaneous resolution: a mover "leaves" its
  // origin before any captures are processed, so it cannot be captured there.
  for (const m of toApply) {
    newSquares[m.from] = { ...newSquares[m.from], piece: null }
  }

  // 4d: Place each allowed mover at their TO square.
  // After step 4c, any TO square previously occupied by a fellow mover is now
  // empty — that mover has already "left". Only stationary pieces remain.
  for (const m of toApply) {
    const piece = state.squares[m.from].piece!  // always read from the original snapshot

    // Capture any stationary enemy at destination
    const existingPiece = newSquares[m.to]?.piece
    if (existingPiece && existingPiece.owner !== m.player) {
      if (!newCapturedBy[m.player]) newCapturedBy[m.player] = []
      newCapturedBy[m.player] = [...newCapturedBy[m.player], existingPiece.type]
    }

    // Build the moved piece (with promotion if applicable)
    let movedPiece: Piece = { ...piece, hasMoved: true }
    if (m.promotion && piece.type === 'pawn') {
      movedPiece = { ...movedPiece, type: m.promotion }
    }

    newSquares[m.to] = { ...newSquares[m.to], piece: movedPiece }

    // Pawn-specific state updates
    if (piece.type === 'pawn') {
      const [fromCol, fromRow] = m.from.split(',').map(Number)
      const [toCol, toRow] = m.to.split(',').map(Number)
      const [dc, dr] = ADVANCE[piece.facing]

      // En passant capture: pawn moved diagonally to the en passant target square
      if (state.players[m.player].enPassantTarget === m.to) {
        const capturedCol = toCol - dc
        const capturedRow = toRow - dr
        const epKey = `${capturedCol},${capturedRow}`
        if (newSquares[epKey]?.piece) {
          if (!newCapturedBy[m.player]) newCapturedBy[m.player] = []
          newCapturedBy[m.player] = [...newCapturedBy[m.player], 'pawn']
          newSquares[epKey] = { ...newSquares[epKey], piece: null }
        }
      }

      // Set en passant target for opposing players after a double advance
      const isDoubleAdvance = Math.abs(toRow - fromRow) === 2 || Math.abs(toCol - fromCol) === 2
      if (isDoubleAdvance) {
        const epCol = fromCol + dc
        const epRow = fromRow + dr
        const epKey = `${epCol},${epRow}`
        for (const p of newPlayers) {
          if (p.index !== m.player) {
            p.enPassantTarget = epKey
          }
        }
      }
    }

    // Castling: king moves exactly 2 squares — also move the accompanying rook
    if (piece.type === 'king') {
      const [fromCol, fromRow] = m.from.split(',').map(Number)
      const [toCol, toRow] = m.to.split(',').map(Number)
      const colDelta = toCol - fromCol
      const rowDelta = toRow - fromRow

      if (Math.abs(colDelta) === 2 && rowDelta === 0) {
        const dir = colDelta > 0 ? 1 : -1
        let c = fromCol + dir
        while (c >= 0 && c < state.boardSize) {
          const rKey = `${c},${fromRow}`
          const rPiece = state.squares[rKey]?.piece  // read original state
          if (rPiece?.type === 'rook' && rPiece.owner === m.player) {
            const rookDest = `${toCol - dir},${fromRow}`
            newSquares[rKey] = { ...newSquares[rKey], piece: null }
            newSquares[rookDest] = { ...newSquares[rookDest], piece: { ...rPiece, hasMoved: true } }
            break
          }
          if (rPiece) break
          c += dir
        }
      } else if (colDelta === 0 && Math.abs(rowDelta) === 2) {
        const dir = rowDelta > 0 ? 1 : -1
        let r = fromRow + dir
        while (r >= 0 && r < state.boardSize) {
          const rKey = `${fromCol},${r}`
          const rPiece = state.squares[rKey]?.piece  // read original state
          if (rPiece?.type === 'rook' && rPiece.owner === m.player) {
            const rookDest = `${fromCol},${toRow - dir}`
            newSquares[rKey] = { ...newSquares[rKey], piece: null }
            newSquares[rookDest] = { ...newSquares[rookDest], piece: { ...rPiece, hasMoved: true } }
            break
          }
          if (rPiece) break
          r += dir
        }
      }

      // Revoke castling rights for king
      newPlayers[m.player].castlingRights = { kingSide: false, queenSide: false }
    }

    // Revoke castling rights for moved rook
    if (piece.type === 'rook') {
      const [fromCol, fromRow] = m.from.split(',').map(Number)
      const playerFacing = state.players[m.player].facing
      if (playerFacing === 'UP' || playerFacing === 'DOWN') {
        if (fromCol >= 4) newPlayers[m.player].castlingRights = { ...newPlayers[m.player].castlingRights, kingSide: false }
        else newPlayers[m.player].castlingRights = { ...newPlayers[m.player].castlingRights, queenSide: false }
      } else {
        if (fromRow >= 4) newPlayers[m.player].castlingRights = { ...newPlayers[m.player].castlingRights, kingSide: false }
        else newPlayers[m.player].castlingRights = { ...newPlayers[m.player].castlingRights, queenSide: false }
      }
    }
  }

  // Step 5: Eliminate players whose king was captured (not on board anymore)
  const playersWithKings = new Set<number>()
  for (const sq of Object.values(newSquares)) {
    if (sq.piece?.type === 'king') playersWithKings.add(sq.piece.owner)
  }
  for (const p of newPlayers) {
    if (p.alive && !playersWithKings.has(p.index)) {
      p.alive = false
    }
  }

  const firstAlive = newPlayers.findIndex(p => p.alive)

  // Build history entries with pieceType snapshot
  const historyEntries = [
    ...toApply.map(m => ({ ...m, pieceType: state.squares[m.from].piece!.type })),
    ...mutualMoves.map(m => ({ ...m, pieceType: state.squares[m.from].piece!.type })),
  ]

  return {
    ...state,
    squares: newSquares,
    players: newPlayers,
    pendingMoves: [],
    turnNumber: state.turnNumber + 1,
    activePlayer: firstAlive >= 0 ? firstAlive : 0,
    moveHistory: [...state.moveHistory, ...historyEntries],
    capturedBy: newCapturedBy,
  }
}
